// why-did-you-render を最初にインポート（開発環境でのみ有効）
import './wdyr'
import './mocks/setupDemoMode'

import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import CssBaseline from '@mui/material/CssBaseline'
import App from './App'
import { DemoApp } from './DemoApp'
import { IntlProvider } from 'react-intl'
import translations from './i18n/locales'
import store from './store'
import { muiTheme } from './styles/muiTheme'
import * as serviceWorker from './serviceWorker'
import './styles/reactDraft.scss'
import globalErrorHandler from './handlers/globalErrorHandler'
import cacheExpirationHandler from './handlers/cacheExpirationHandler'
import authorizationHandler from './handlers/authorizationHandler'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise'
import { extractRootDomainName } from './utils/urls'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { Router, history } from './view/router'
import { isCommonAssetRequest } from 'msw'

ModuleRegistry.registerModules([AllCommunityModule, AllEnterpriseModule])

// デモ（sevend-demo.net）は .env.demo-staging で FLAGXS_ENV=production のためこの条件でカバーされる。
// ローカルのデモ動作確認（.env.demo-development）は FLAGXS_ENV=development のため対象外。
if (
  process.env.FLAGXS_ENV === 'production' ||
  process.env.FLAGXS_ENV === 'staging'
) {
  if (!process.env.FLAGXS_AG_GRID_LICENSE_KEY) {
    throw new Error(
      `AgGrid licence key is required in ${process.env.FLAGXS_ENV}.`
    )
  }
  LicenseManager.setLicenseKey(process.env.FLAGXS_AG_GRID_LICENSE_KEY)
}

// Check host domain.
const HOST_DOMAINS = process.env
  .FLAGXS_HOST_DOMAINS!.split(',')
  .map(v => v.trim())
const domain = extractRootDomainName()
if (!HOST_DOMAINS.includes(domain)) {
  throw new Error(
    `${domain} is unknown host for the build profile: ${process.env.FLAGXS_ENV}`
  )
}

globalErrorHandler.register()
cacheExpirationHandler.register()
authorizationHandler.register()

// Setup NewRelic.
if (
  process.env.FLAGXS_NEWRELIC_RELEASE_NAME &&
  process.env.FLAGXS_NEWRELIC_RELEASE_ID
) {
  newrelic.addRelease(
    process.env.FLAGXS_NEWRELIC_RELEASE_NAME,
    process.env.FLAGXS_NEWRELIC_RELEASE_ID
  )
}

// language without region code
const language = navigator.language.split(/[-_]/)[0]
const messages = translations[language] || translations.en

const renderApp = () => {
  const root = createRoot(document.getElementById('root')!)
  root.render(
    <IntlProvider key={language} locale={language} messages={messages}>
      <ThemeProvider theme={muiTheme}>
        <StyledEngineProvider injectFirst={true}>
          <Provider store={store}>
            <Router history={history}>
              <CssBaseline />
              {process.env.FLAGXS_DEMO_MODE === 'true' ? <DemoApp /> : <App />}
            </Router>
          </Provider>
        </StyledEngineProvider>
      </ThemeProvider>
    </IntlProvider>
  )

  // serviceWorker.unregister() targets navigator.serviceWorker.ready, which
  // in demo mode is the MSW mock worker itself — calling it here would
  // unregister MSW right after worker.start() resolves, so every API call
  // slips through to a real (nonexistent) backend and hangs forever.
  if (process.env.FLAGXS_DEMO_MODE !== 'true') {
    serviceWorker.unregister()
  }
}

// Shown when the MSW mock worker fails to start (e.g. the browser refuses
// to register the service worker, or public/mockServiceWorker.js is
// missing/stale) — the app has nothing to fall back to in demo mode since
// there is no real backend to serve its requests, so it can't render.
const renderDemoModeUnavailable = (error: unknown): void => {
  // eslint-disable-next-line no-console -- only path left to surface why demo mode failed to start
  console.error('Failed to start MSW mock worker for demo mode.', error)
  const root = document.getElementById('root')!
  root.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;padding:16px;">
      <div>
        <h1 style="font-size:20px;">デモ環境を利用できません</h1>
        <p>モックサーバーの起動に失敗しました。ページを再読み込みしても解決しない場合は、開発担当者にお問い合わせください。</p>
      </div>
    </div>
  `
}

// ページの直接アクセス／リロードで飛ぶHTMLナビゲーションリクエストは
// モックAPIの対象ではないため、'error' 判定から除外してブラウザに委譲する
// （rsbuild dev server が実ファイルを返す）。静的アセットの除外は msw 標準の
// isCommonAssetRequest に委ねる。
// Service Worker 経由では request.mode が 'navigate' として伝播しない
// 場合があるため、mode ではなく Accept ヘッダー（HTMLナビゲーションは
// 'text/html' を含む）とオリジンで判定する。
const isBrowserOwnedRequest = (request: Request): boolean => {
  const url = new URL(request.url)
  if (url.origin !== location.origin) {
    return false
  }
  if (isCommonAssetRequest(request)) {
    return true
  }
  return (request.headers.get('accept') || '').includes('text/html')
}

// In demo mode, wait for the MSW mock worker to be ready before rendering
// so the app's earliest requests don't slip through to a real backend.
if (process.env.FLAGXS_DEMO_MODE === 'true') {
  import('./mocks/browser')
    .then(({ worker }) =>
      worker.start({
        onUnhandledRequest: (request, print) => {
          if (isBrowserOwnedRequest(request)) {
            return
          }
          print.error()
        },
      })
    )
    .then(renderApp)
    .catch(renderDemoModeUnavailable)
} else {
  renderApp()
}
